const EMPTY_ADDRESS = { lines: [], complete: false };
const DOMESTIC_COUNTRIES = new Set([
    'us',
    'usa',
    'u.s.',
    'u.s.a.',
    'united states',
    'united states of america',
]);
function clean(value) {
    if (value == null)
        return '';
    const text = String(value).replace(/\s+/g, ' ').trim();
    if (!text || /^(n\/a|null|undefined|none|-)$/i.test(text))
        return '';
    return text;
}
function isDomesticCountry(value) {
    return DOMESTIC_COUNTRIES.has(value.toLowerCase());
}
function partsFromObject(record) {
    const street = clean(record.address_1) ||
        clean(record.line1) ||
        clean(record.address1) ||
        clean(record.street) ||
        (typeof record.address === 'string' ? clean(record.address) : '');
    return {
        line1: street,
        line2: clean(record.address_2) || clean(record.line2) || clean(record.address2),
        city: clean(record.city),
        state: clean(record.state),
        postal: clean(record.zipcode) ||
            clean(record.zip) ||
            clean(record.postal_code) ||
            clean(record.postalCode),
        country: clean(record.country),
    };
}
function linesFromParts(parts) {
    const stateZip = [parts.state, parts.postal].filter(Boolean).join(' ');
    const locality = [parts.city, stateZip].filter(Boolean).join(', ');
    const country = parts.country && !isDomesticCountry(parts.country) ? parts.country : '';
    const lines = [parts.line1, parts.line2, locality, country].filter(Boolean);
    return {
        lines,
        complete: Boolean(parts.line1 && (parts.city || parts.postal)),
    };
}
/**
 * Formats a mailing address for an invoice. Blank fields are omitted.
 * A domestic country is omitted; any other country is printed.
 */
export function formatMailingAddress(address) {
    if (Array.isArray(address)) {
        for (const item of address) {
            const formatted = formatMailingAddress(item);
            if (formatted.lines.length)
                return formatted;
        }
        return EMPTY_ADDRESS;
    }
    if (typeof address === 'string') {
        const lines = address
            .split(',')
            .map((part) => clean(part))
            .filter((part) => part && !isDomesticCountry(part));
        return {
            lines,
            complete: lines.length >= 2,
        };
    }
    if (!address || typeof address !== 'object')
        return EMPTY_ADDRESS;
    return linesFromParts(partsFromObject(address));
}
function readDetails(details) {
    if (!details)
        return {};
    if (typeof details === 'string') {
        try {
            const parsed = JSON.parse(details);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                ? parsed
                : {};
        }
        catch {
            return {};
        }
    }
    if (typeof details === 'object' && !Array.isArray(details)) {
        return details;
    }
    return {};
}
function chargeBillingAddress(order) {
    const sources = [
        order.intentDetails,
        order.paymentIntent,
        order.payment_intent,
    ];
    for (const source of sources) {
        if (!source || typeof source !== 'object')
            continue;
        const charges = source.charges?.data;
        if (!Array.isArray(charges))
            continue;
        for (const charge of charges) {
            if (!charge || typeof charge !== 'object')
                continue;
            const address = charge
                .billing_details?.address;
            if (address)
                return address;
        }
    }
    return null;
}
function collectCandidates(order) {
    const details = readDetails(order.details);
    const user = order.users_permissions_user &&
        typeof order.users_permissions_user === 'object'
        ? order.users_permissions_user
        : undefined;
    const profile = order.profile && typeof order.profile === 'object'
        ? order.profile
        : undefined;
    const values = [
        chargeBillingAddress(order),
        order.billingAddress,
        order.billing_address,
        details.billingAddress,
        details.billing_address,
        user?.profile?.address,
        profile?.address,
        order.address,
        order.customerAddress,
    ];
    const candidates = [];
    for (const value of values) {
        const formatted = formatMailingAddress(value);
        if (formatted.lines.length)
            candidates.push(formatted);
    }
    return candidates;
}
/**
 * Customer mailing address for an invoice.
 * Prefers a complete billing snapshot from the original charge, then the
 * buyer profile. Shipping addresses are ignored. Nothing is invented.
 */
export function getInvoiceCustomerMailingAddress(order) {
    if (!order || typeof order !== 'object')
        return EMPTY_ADDRESS;
    const candidates = collectCandidates(order);
    const complete = candidates.find((candidate) => candidate.complete);
    if (complete)
        return complete;
    if (candidates.length)
        return { lines: candidates[0].lines, complete: false };
    return EMPTY_ADDRESS;
}
